import { Insight } from "@stratify/shared";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../config/firebase";
import apiClient from "../../shared/apiClient";

export const insightsService = {
  // Read operations remain unchanged, using direct Firestore access
  async getInsightsByDocument(documentId: string): Promise<Insight[]> {
    const insightsRef = collection(db, "insights");
    const q = query(insightsRef, where("documentId", "==", documentId));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => ({
      ...doc.data(),
      id: doc.id,
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate(),
    })) as Insight[];
  },

  // Create insight using REST API
  async createInsight(
    documentId: string,
    insightData: Omit<Insight, "id" | "createdAt" | "updatedAt" | "documentId">
  ): Promise<Insight> {
    if (!insightData.userId) {
      throw new Error("Insight data must include userId for creation.");
    }

    const response = await apiClient.post<Insight>(
      `/documents/${documentId}/insights`,
      {
        ...insightData,
        documentId,
        viewed: true, // If a user creates an insight, they've viewed it
      }
    );

    return response.data;
  },

  // Update insight using REST API
  async updateInsight(
    documentId: string,
    insightId: string,
    updates: Partial<
      Omit<Insight, "id" | "documentId" | "createdAt" | "updatedAt">
    >
  ): Promise<Insight> {
    const response = await apiClient.put<Insight>(
      `/documents/${documentId}/insights/${insightId}`,
      updates
    );

    return response.data;
  },

  // Delete insight using REST API
  async deleteInsight(documentId: string, insightId: string): Promise<void> {
    await apiClient.delete(`/documents/${documentId}/insights/${insightId}`);
  },

  // Read operation remains unchanged, using direct Firestore access
  async getInsightById(insightId: string): Promise<Insight> {
    const docRef = doc(db, "insights", insightId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return {
        ...docSnap.data(),
        id: docSnap.id,
        createdAt: docSnap.data().createdAt?.toDate(),
        updatedAt: docSnap.data().updatedAt?.toDate(),
      } as Insight;
    }

    throw new Error(`Insight with ID ${insightId} not found.`);
  },
};
